import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App';
import './styles/globals.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

// Auto-recover from stale lazy-chunk loads after a deploy. Routes are code-split
// (`lazy(() => import(...))`), so when a hashed chunk (e.g. UsersPage-*.js) is
// replaced by a newer build, a still-open client fetches a chunk that no longer
// exists — Vite fires `vite:preloadError` and the route hard-fails. Reload once
// to pull the fresh index.html + chunks. The timestamp guard reloads only if the
// last attempt was >15s ago, so a genuinely-broken chunk can't cause a loop while
// a later deploy can still recover.
const CHUNK_RELOAD_GUARD = 'rheora:chunk-reload-at';
window.addEventListener('vite:preloadError', (event) => {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD) ?? '0');
  if (Date.now() - last < 15_000) return; // recently reloaded — let it surface
  event.preventDefault();
  sessionStorage.setItem(CHUNK_RELOAD_GUARD, String(Date.now()));
  window.location.reload();
});

/**
 * Optional MSW (Mock Service Worker) bootstrap. Active only when:
 *   - `import.meta.env.DEV` is true (production builds tree-shake the
 *     conditional import away), AND
 *   - `VITE_BILLING_MOCK === 'true'` in `.env.development`.
 *
 * Covers Epic B6 (PDF download) until the backend `GET /v1/bills/{billId}/pdf`
 * endpoint ships. Documented in `docs/us/epics/us-billing.md` §B6.
 * Disable by flipping the env flag to `false` — no source changes required.
 *
 * `VITE_ESI_SEARCH_FALLBACK` used to be a SECOND trigger here, and it defaulted
 * to on — so a service worker was installed in every dev session whether or not
 * anyone had asked for a mock. It armed a fallback for `/v1/us/esi-search`,
 * which Quotation shipped in E39; both the flag and the handler are gone.
 */
async function bootstrap(): Promise<void> {
  const mockWorkerWanted = import.meta.env.VITE_BILLING_MOCK === 'true';
  if (import.meta.env.DEV && mockWorkerWanted) {
    const { startBillingMockWorker } = await import('./test/msw/browser');
    await startBillingMockWorker();
  }
  createRoot(rootEl as HTMLElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
